import { useState } from "react";
import emailjs from "@emailjs/browser";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Github, Linkedin, Mail, FileDown } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import SocialButton from "@/components/ui/social-button";

// Tipado estricto para los datos del formulario
interface ContactFormData {
  name: string;
  email: string;
  message: string;
}

const initialFormData: ContactFormData = {
  name: "",
  email: "",
  message: "",
};


const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState<ContactFormData>(initialFormData);
  const [errors, setErrors] = useState<Partial<ContactFormData>>({});
  const [isSending, setIsSending] = useState(false);
  // Honeypot anti-spam
  const [honeypot, setHoneypot] = useState("");

  // Validación simple de campos
  const validate = (data: ContactFormData) => {
    const newErrors: Partial<ContactFormData> = {};
    if (!data.name.trim()) newErrors.name = "El nombre es obligatorio.";
    if (!data.email.trim()) newErrors.email = "El email es obligatorio.";
    else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(data.email))
      newErrors.email = "Email inválido.";
    if (!data.message.trim()) newErrors.message = "El mensaje es obligatorio.";
    return newErrors;
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Honeypot: si el campo oculto tiene valor, abortar (probable bot)
    if (honeypot) return;
    const validationErrors = validate(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    // Leer variables de entorno
    const serviceId = import.meta.env.VITE_EMAILJS_SERVICE_ID;
    const templateId = import.meta.env.VITE_EMAILJS_TEMPLATE_ID;
    const publicKey = import.meta.env.VITE_EMAILJS_PUBLIC_KEY;

    if (!serviceId || !templateId || !publicKey) {
      toast({
        variant: "destructive",
        title: "Email service not configured",
        description: "Please contact me directly. (Missing EmailJS config)",
      });
      return;
    }

    try {
      setIsSending(true);
      await emailjs.send(
        serviceId,
        templateId,
        {
          name: formData.name,
          email: formData.email,
          message: formData.message,
        },
        {
          publicKey,
          blockHeadless: true,
          limitRate: { throttle: 15000 },
        }
      );
      toast({
        title: "Message Sent!",
        description: "Thanks for reaching out. I'll get back to you soon!",
      });
      setFormData(initialFormData);
      setErrors({});
    } catch (err) {
      console.error("EmailJS error", err);
      toast({
        variant: "destructive",
        title: "Failed to send message",
        description: "Please try again later or email me directly.",
      });
    } finally {
      setIsSending(false);
    }
  };


  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  // Honeypot handler
  const handleHoneypot = (e: React.ChangeEvent<HTMLInputElement>) => {
    setHoneypot(e.target.value);
  };

  return (
    <section id="contact" className="py-24 bg-secondary/50">
      <div className="container mx-auto px-6">
        <h2 className="text-4xl font-bold mb-4 text-center">
          Let's <span className="text-gradient">Connect</span>
        </h2>
        <p className="text-center text-muted-foreground mb-2">
          Open to freelance and full-time opportunities
        </p>
        <p className="text-center text-muted-foreground/80 mb-12 text-sm">
          Let's build something great together
        </p>

        <div className="max-w-2xl mx-auto">
          <form onSubmit={handleSubmit} className="space-y-6 mb-8" noValidate autoComplete="off">
            {/* Honeypot anti-spam (invisible para usuarios reales) */}
            <input
              type="text"
              name="company"
              value={honeypot}
              onChange={handleHoneypot}
              tabIndex={-1}
              autoComplete="off"
              className="hidden"
              aria-hidden="true"
            />
            <div>
              <Input
                name="name"
                placeholder="Your Name"
                value={formData.name}
                onChange={handleChange}
                required
                aria-invalid={!!errors.name}
                aria-describedby={errors.name ? "name-error" : undefined}
                className="bg-card border-border focus:border-primary"
              />
              {errors.name && (
                <p
                  id="name-error"
                  className="text-red-500 text-xs mt-1"
                >
                  {errors.name}
                </p>
              )}
            </div>
            <div>
              <Input
                name="email"
                type="email"
                placeholder="your.email@example.com"
                value={formData.email}
                onChange={handleChange}
                required
                aria-invalid={!!errors.email}
                aria-describedby={errors.email ? "email-error" : undefined}
                className="bg-card border-border focus:border-primary"
              />
              {errors.email && (
                <p
                  id="email-error"
                  className="text-red-500 text-xs mt-1"
                >
                  {errors.email}
                </p>
              )}
            </div>
            <div>
              <Textarea
                name="message"
                placeholder="Your message..."
                value={formData.message}
                onChange={handleChange}
                required
                rows={6}
                aria-invalid={!!errors.message}
                aria-describedby={errors.message ? "message-error" : undefined}
                className="bg-card border-border focus:border-primary resize-none"
              />
              {errors.message && (
                <p
                  id="message-error"
                  className="text-red-500 text-xs mt-1"
                >
                  {errors.message}
                </p>
              )}
            </div>
            <Button
              type="submit"
              size="lg"
              className="w-full bg-primary text-primary-foreground hover:glow-effect transition-all font-medium"
              disabled={isSending}
            >
              {isSending ? "Sending..." : "Send Message"}
            </Button>
          </form>

          {/* Resume Download Link */}
          <div className="text-center mb-8">
            <a
              href="/assets/Lucas_Fernandez_Fullstack.pdf"
              download="Lucas_Fernandez_Fullstack_Javascript.pdf"
              aria-label="Download resume PDF"
              className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors group"
            >
              <FileDown className="w-4 h-4 group-hover:animate-bounce" />
              Prefer a PDF? Download my resume
              <span className="text-xs text-muted-foreground/60">(Updated — 2026)</span>
            {/* // TODO: Si recibís mucho spam, integrar reCAPTCHA v2 de EmailJS y enviar el token como "g-recaptcha-response" en el objeto de datos. */}
            </a>
          </div>

          <div className="flex justify-center gap-6">
            <SocialButton
              href="https://github.com/lucasfer01"
              label="GitHub"
            >
              <Github className="w-6 h-6" />
            </SocialButton>
            <SocialButton
              href="https://linkedin.com/in/lucasfernandez-dev/"
              label="LinkedIn"
            >
              <Linkedin className="w-6 h-6" />
            </SocialButton>
            <SocialButton
              href="mailto:lucasfer9912@gmail.com"
              label="Email"
            >
              <Mail className="w-6 h-6" />
            </SocialButton>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
